import React, { useId, useMemo, useCallback } from 'react'
import {
  Controller,
  useFieldArray,
  UseFieldArrayRemove,
  useFormContext,
} from 'react-hook-form'
import { FiltersSchema } from '../filtersSchema'
import {
  AsyncSelect,
  Box,
  Button,
  Field,
  Flex,
  useAppTheme,
} from '@workwhile/ui'
import { CircleMinus } from 'lucide-react'
import { usePositionsContextValue } from 'pages/Positions/Positions/context/PositionsProvider'
import { indexBy, prop } from 'ramda'
import { FullOptions, MatchData, Searcher } from 'fast-fuzzy'

const AddedCompanyGroupRow = ({
  remove,
  index,
  value,
  isLast,
}: {
  remove: UseFieldArrayRemove
  index: number
  value: string
  isLast: boolean
}) => {
  const { colors, radii, space } = useAppTheme()
  return (
    <Flex
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
      gap={space[2]}
      mb={isLast ? 0 : space[2]}
    >
      <Flex
        alignItems="flex-start"
        justifyContent="space-between"
        border="1px solid"
        borderColor={colors.neutrals[100]}
        p={2}
        borderRadius={radii.small}
        width="100%"
        position="relative"
      >
        {value}
      </Flex>
      <Button
        onClick={() => remove(index)}
        variant="text"
        color="red"
        size="small"
        danger
        style={{
          margin: 0,
          height: '1.6rem',
        }}
      >
        <CircleMinus size={16} />
      </Button>
    </Flex>
  )
}

const AddedCompanyGroupIds = () => {
  const baseId = useId()
  const { watch, control } = useFormContext<FiltersSchema>()
  const {
    state: { companyGroups },
  } = usePositionsContextValue()

  const { remove } = useFieldArray({
    name: 'company.companyGroupIds',
    control,
  })

  const companyGroupIds = watch('company.companyGroupIds') ?? []

  const companyGroupMap = useMemo(() => {
    if (!companyGroups) return {}
    return indexBy(prop('id'), companyGroups)
  }, [companyGroups])

  const addedCompanyGroupRows = companyGroupIds.map(({ id }, index) => (
    <AddedCompanyGroupRow
      key={`added-company-group-${baseId}-${id}-${index}`}
      remove={remove}
      index={index}
      value={`${companyGroupMap[id]?.name} (${id})`}
      isLast={index === companyGroupIds.length - 1}
    />
  ))
  return (
    <Box width="256px" maxHeight="360px" overflow="auto">
      {addedCompanyGroupRows}
    </Box>
  )
}

type CompanyGroupOption = { label: string; value: number }

const fuzzyOptions: FullOptions<CompanyGroupOption> = {
  threshold: 0.7,
  ignoreCase: true,
  ignoreSymbols: true,
  normalizeWhitespace: true,
  returnMatchData: false,
  keySelector: (obj) => [obj.label, obj.value.toString()],
}

export const CompanyGroupIdsFilter = () => {
  const { control, trigger, getValues, setValue, resetField } =
    useFormContext<FiltersSchema>()
  const {
    state: { companyGroups },
  } = usePositionsContextValue()

  const companyGroupOptions = useMemo(() => {
    return companyGroups?.map((companyGroup) => ({
      label: `${companyGroup.name} (${companyGroup.id})`,
      value: companyGroup.id,
    }))
  }, [companyGroups])

  const searcher = useMemo(() => {
    return new Searcher<CompanyGroupOption, FullOptions<CompanyGroupOption>>(
      companyGroupOptions ?? [],
      fuzzyOptions
    )
  }, [companyGroupOptions])

  const loadOptions = useCallback(
    (rawInput: string) => {
      if (!rawInput) return true

      const results = searcher.search(
        rawInput,
        fuzzyOptions
      ) as unknown as MatchData<CompanyGroupOption>[]

      return new Promise((resolve) => {
        resolve(results)
      })
    },
    [searcher]
  )

  const { append } = useFieldArray({
    name: 'company.companyGroupIds',
    control,
  })

  return (
    <Box>
      <Controller
        control={control}
        name="company.companyGroupIdUserInput"
        defaultValue={undefined}
        render={({ field, fieldState: { error, invalid } }) => (
          <Field error={error?.message?.toString()}>
            <Flex gap={2} width="256px" maxHeight="360px">
              <AsyncSelect
                block
                {...field}
                isClearable
                isSearchable
                defaultOptions={companyGroupOptions}
                loadOptions={loadOptions}
                value={
                  companyGroupOptions.find(
                    (option) => option.value === field.value
                  ) || null
                }
                onChange={(option) => {
                  field.onChange(option?.value)
                }}
                styles={{
                  container: (base) => ({
                    ...base,
                    width: '100%',
                  }),
                }}
              />
              <Button
                variant="primary"
                disabled={invalid}
                onClick={async () => {
                  const isInputValid = await trigger(
                    'company.companyGroupIdUserInput'
                  )
                  const companyGroupId = Number(
                    getValues('company.companyGroupIdUserInput')
                  )
                  const addedCompanyGroups =
                    getValues('company.companyGroupIds') ?? []
                  const doesInputExistInCurrentGroup = addedCompanyGroups.some(
                    (companyGroup) => companyGroup.id === companyGroupId
                  )
                  const isValidNumber = !Number.isNaN(companyGroupId)

                  const isValid =
                    isInputValid &&
                    !doesInputExistInCurrentGroup &&
                    isValidNumber

                  if (isValid) {
                    append({ id: companyGroupId })
                    resetField('company.companyGroupIdUserInput', {
                      defaultValue: undefined,
                    })
                    setValue('company.companyGroupIdUserInput', undefined)
                  }

                  if (doesInputExistInCurrentGroup) {
                    resetField('company.companyGroupIdUserInput', {
                      defaultValue: undefined,
                    })
                  }
                }}
              >
                Add
              </Button>
            </Flex>
          </Field>
        )}
      />

      <AddedCompanyGroupIds />
    </Box>
  )
}
